// Static task metadata for incentive surfaces.
// Reward amounts come from API /v1/incentive/state — DO NOT hardcode here.
// This file owns: titles, descriptions, button text, external URLs.

export type TaskType =
  | "fund_treasury"
  | "add_connector"
  | "share_workflow"
  | "follow_x"
  | "star_github"
  | "subscribe_youtube";

export interface TaskMeta {
  type: TaskType;
  title: string;
  description: string;
  isInternal: boolean; // true → silent auto-verify on page load
  externalURL?: string; // for external tasks
  oauthProvider?: "twitter" | "github" | "google"; // betterauth provider
  oauthScopes?: string[]; // for incremental scope (Google YouTube)
  buttonTextNotLinked?: string;
  buttonTextLinked?: string;
  buttonTextInternal?: string; // for internal tasks
}

export const TASKS: TaskMeta[] = [
  {
    type: "fund_treasury",
    title: "Fund Treasury",
    description: "Add native token to your Treasury wallet on any supported chain",
    isInternal: false,
    buttonTextLinked: "Show Treasury address",
  },
  {
    type: "add_connector",
    title: "Add Connector",
    description: "Connect a third-party service (Slack, GitHub, etc.) to your org",
    isInternal: true,
    externalURL: "/connectors", // internal navigation
    buttonTextInternal: "Browse connectors",
  },
  {
    type: "share_workflow",
    title: "Share a Workflow",
    description: "Share one of your workflows publicly",
    isInternal: true,
    externalURL: "/workflows",
    buttonTextInternal: "Open workflows",
  },
  {
    type: "follow_x",
    title: "Follow B3 on X",
    description: "Stay updated on new features and launches",
    isInternal: false,
    externalURL: "https://x.com/b3dotfun",
    oauthProvider: "twitter",
    buttonTextNotLinked: "Connect X account",
    buttonTextLinked: "Open X",
  },
  {
    type: "star_github",
    title: "Star B3 on GitHub",
    description: "Check out our open source code",
    isInternal: false,
    externalURL: "https://github.com/b3-fun/b3os",
    oauthProvider: "github",
    buttonTextNotLinked: "Connect GitHub",
    buttonTextLinked: "Open GitHub",
  },
  {
    type: "subscribe_youtube",
    title: "Subscribe to B3 YouTube",
    description: "Watch tutorials and product demos",
    isInternal: false,
    // Keep in sync with backend channel id at
    // services/b3os-workflow/configs/config.yaml incentive.external.b3_youtube_channel_id
    externalURL: "https://www.youtube.com/@B3OS_AI",
    oauthProvider: "google",
    oauthScopes: ["https://www.googleapis.com/auth/youtube.readonly"],
    buttonTextNotLinked: "Grant YouTube permission",
    buttonTextLinked: "Open YouTube",
  },
];

// Lookup helper so callers don't repeat O(N) `TASKS.find(...)` on every render.
// Returns undefined when the API surfaces a task type the frontend doesn't know
// about (e.g., a backend-only debug task or a future task added before the FE
// roll-out catches up).
export const TASKS_BY_TYPE: Record<string, TaskMeta | undefined> = Object.fromEntries(TASKS.map(t => [t.type, t]));
